import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import foxData from './experiences/data/FoxExperience';
import quickwashData from './experiences/data/QuickWashExperience';
import antologyData from './experiences/data/AntologyExperience';
import chipotleData from './experiences/data/ChipotleExperience';
import miraData from './experiences/data/MiraExperience';
import qbciData from './experiences/data/QBCIExperience';
import pomuData from './experiences/data/PomuExperience';

const experiences = [
  foxData,
  quickwashData,
  antologyData,
  chipotleData,
  miraData,
  qbciData,
  pomuData,
];

const ExperienceRow = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const location = useLocation();
  const scrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState({ left: false, right: false });

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowArrows({
        left: scrollLeft > 10,
        right: scrollLeft < scrollWidth - clientWidth - 10
      });
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="professional" className="py-8 bg-black">
      <div className="px-8 md:px-12 lg:px-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
          Experience
        </h2>

        <div className="relative group">
          {/* Left Arrow */}
          {showArrows.left && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-40 group"
              aria-label="Scroll left"
            >
              <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
            </button>
          )}

          {/* Right Arrow */}
          {showArrows.right && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-40 group"
              aria-label="Scroll right"
            >
              <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          )}

          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {experiences.map((exp) => (
              <Link
                key={exp.slug}
                to={`/experience/${exp.slug}`}
                state={{ background: location }}
                className="flex-shrink-0 w-[350px] md:w-[400px] relative group/card cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg"
                onMouseEnter={() => setHoveredCard(exp.slug)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  className={`relative h-full rounded-lg overflow-hidden transition-all duration-300 ${hoveredCard === exp.slug
                    ? 'transform scale-105 z-20 shadow-2xl shadow-black/50'
                    : 'z-10'
                    }`}
                >
                  <div
                    className="absolute inset-0 w-full h-full"
                    style={{
                      background: `url(${exp.poster}) center/cover`,
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                  <div className="relative p-6 h-full flex flex-col justify-end min-h-[400px]">
                    <div>
                      <div className="flex items-center gap-4 mb-2">
                        <h3 className="text-3xl md:text-4xl font-bold text-white">
                          {exp.posterTitle || exp.company}
                        </h3>

                        <span
                          className={`bg-white text-black py-2 px-4 rounded-md font-semibold transition-all duration-300 flex-shrink-0 hover:bg-gray-200 ${hoveredCard === exp.slug ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
                            }`}
                        >
                          Learn More
                        </span>
                      </div>

                      <p className="text-lg font-semibold text-gray-200">
                        {exp.roles[0].title}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceRow;
